import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { supabase } from 'lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  readCredentialSetupKindFromBrowser,
  readTokenHashFromBrowser,
  type CredentialSetupKind,
} from '../../shared/auth/credentialSetupFlow';
import { brand } from '../../shared/config/brand';
import { RelixLockup } from '../../components/brand/RelixLogo';
import { DeliveryAuditSection } from '../../components/marketing/solutions/DeliveryAuditForm';
import { trackEvent } from '../../shared/analytics';

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    signIn,
    sendPasswordResetEmail,
    user,
    organizations,
    currentOrg,
    loading: authLoading,
    initialized,
    refreshUser,
  } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [credentialSetupKind, setCredentialSetupKind] = useState<CredentialSetupKind | null>(() =>
    readCredentialSetupKindFromBrowser()
  );
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryConfirm, setRecoveryConfirm] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [showPostInviteSignInHint, setShowPostInviteSignInHint] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const credentialSetupActive = credentialSetupKind !== null;

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setCredentialSetupKind('recovery');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Custom email-template flow: exchange token_hash via verifyOtp
  // This handles links like /auth?token_hash=...&type=recovery that bypass
  // Supabase's /verify endpoint (avoids token consumption by email scanners).
  useEffect(() => {
    let cancelled = false;
    const tokenInfo = readTokenHashFromBrowser();
    if (!tokenInfo) return;

    (async () => {
      const { error: otpErr } = await supabase.auth.verifyOtp({
        token_hash: tokenInfo.token_hash,
        type: tokenInfo.type as 'recovery' | 'invite',
      });
      if (cancelled) return;
      if (otpErr) {
        console.error('[AuthPage] verifyOtp failed:', otpErr.message);
        setRecoveryError(
          tokenInfo.type === 'invite'
            ? 'This invitation link is invalid or has expired. Ask your admin for a new invite.'
            : 'This reset link is invalid or has expired. Request a new one from the sign-in page.'
        );
        return;
      }
      // verifyOtp fires onAuthStateChange(PASSWORD_RECOVERY) which sets credentialSetupKind.
      // Clean the URL so a page refresh doesn't retry the exchange.
      window.history.replaceState(null, '', window.location.pathname);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncFromUrl = () => {
      const next = readCredentialSetupKindFromBrowser();
      if (next) setCredentialSetupKind(next);
    };
    syncFromUrl();
    window.addEventListener('hashchange', syncFromUrl);
    return () => window.removeEventListener('hashchange', syncFromUrl);
  }, []);

  useEffect(() => {
    if (credentialSetupActive) return;
    if (!user || !initialized || authLoading) return;
    if (organizations.length > 0) {
      navigate('/analytics', { replace: true });
    } else {
      navigate('/onboarding', { replace: true });
    }
  }, [user, initialized, authLoading, organizations, navigate, credentialSetupActive]);

  const orgInviteLabel = useMemo(() => {
    const o = currentOrg || organizations[0];
    const n = o?.display_name?.trim() || o?.name?.trim();
    return n || 'your organization';
  }, [currentOrg, organizations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { error: signInError } = await signIn(formData.email, formData.password);
      if (signInError) {
        setError(signInError);
      } else {
        setShowPostInviteSignInHint(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError(null);
    if (recoveryPassword.length < 6) {
      setRecoveryError('Use at least 6 characters');
      return;
    }
    if (recoveryPassword !== recoveryConfirm) {
      setRecoveryError('Passwords do not match');
      return;
    }
    setRecoveryLoading(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({
        password: recoveryPassword,
      });
      if (updateErr) {
        setRecoveryError(updateErr.message);
        return;
      }

      if (credentialSetupKind === 'invite') {
        const email = user?.email ?? '';
        await supabase.auth.signOut();
        setCredentialSetupKind(null);
        setRecoveryPassword('');
        setRecoveryConfirm('');
        window.history.replaceState(null, '', `${window.location.pathname}`);
        setFormData((fd) => ({ ...fd, email }));
        setIsSignUp(false);
        setShowPostInviteSignInHint(true);
        return;
      }

      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      // Refresh user/org data first so routing guards see up-to-date state.
      await refreshUser();
      // Existing users resetting their password already have orgs and onboarding complete.
      // Navigate directly to /analytics to avoid RequireOrg bouncing to /onboarding
      // during the auth state reconciliation window.
      // Clear credential setup kind AFTER navigation so the useEffect redirect guard
      // (lines 101-109) stays suppressed (credentialSetupActive === true) until we land.
      navigate('/analytics', { replace: true });
      setCredentialSetupKind(null);
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.email.trim()) {
      setError('Enter your email address to receive a reset link.');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      const { error: forgotErr } = await sendPasswordResetEmail(formData.email);
      if (forgotErr) {
        setError(forgotErr);
      } else {
        setForgotPasswordSent(true);
        setError(null);
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const backToSignIn = () => {
    setShowForgotPassword(false);
    setForgotPasswordSent(false);
    setError(null);
    setSuccessMessage(null);
  };

  if (credentialSetupActive) {
    if (!initialized || authLoading) {
      return (
        <div className="auth-page">
          <div className="auth-container animate-fade-in">
            <div className="auth-form-section animate-slide-right">
              <div className="auth-form-wrapper">
                <div className="auth-header">
                  <h1 className="auth-brand-heading">
                    <RelixLockup height={39} />
                  </h1>
                  <p>Loading…</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (!user) {
      const isInvite = credentialSetupKind === 'invite';
      return (
        <div className="auth-page">
          <div className="auth-container animate-fade-in">
            <div className="auth-form-section animate-slide-right">
              <div className="auth-form-wrapper">
                <div className="auth-header">
                  <h1>{isInvite ? 'Link no longer valid' : 'Reset password'}</h1>
                  <p>
                    {isInvite
                      ? 'This link has expired. Ask your organization admin for a new invitation.'
                      : 'This reset link is invalid or has expired. Sign in and request a new link from Settings → My Profile, or use Sign in if you still know your password.'}
                  </p>
                </div>
                <button type="button" className="submit-btn" onClick={() => navigate('/auth')}>
                  Back to sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isInvite = credentialSetupKind === 'invite';
    return (
      <div className="auth-page">
        <div className="auth-container animate-fade-in">
          <div className="auth-form-section animate-slide-right">
            <div className="auth-form-wrapper">
              <div className="auth-header">
                <h1>{isInvite ? `Finish joining ${orgInviteLabel}` : 'Set a new password'}</h1>
                <p>
                  {isInvite
                    ? `Create a password for ${brand.name}\u2014you'll use it to sign in from now on. This isn't \"forgot password\"; you're finishing setup after an invitation.`
                    : 'Choose a new password for your account.'}
                </p>
              </div>
              {recoveryError ? <div className="auth-error">{recoveryError}</div> : null}
              <form onSubmit={handleCredentialSubmit}>
                <div className="form-field">
                  <label htmlFor="recovery-password">
                    {isInvite ? 'Create password' : 'New password'}
                  </label>
                  <div className="input-wrapper">
                    <Lock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="recovery-password"
                      value={recoveryPassword}
                      onChange={(e) => setRecoveryPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="form-field">
                  <label htmlFor="recovery-confirm">
                    {isInvite ? 'Confirm password' : 'Confirm new password'}
                  </label>
                  <div className="input-wrapper">
                    <Lock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="recovery-confirm"
                      value={recoveryConfirm}
                      onChange={(e) => setRecoveryConfirm(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <button type="submit" className="submit-btn" disabled={recoveryLoading}>
                  {recoveryLoading ? 'Saving…' : isInvite ? 'Save password' : 'Update password'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
              {isInvite ? (
                <p
                  style={{
                    marginTop: '1.25rem',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Wrong email? Contact the person who invited you.
                </p>
              ) : null}
            </div>
          </div>
          <div className="auth-visual-section">
            <div className="visual-content animate-fade-in">
              <div className="visual-glow" />
              <h2>{isInvite ? 'Almost there' : 'Secure your account'}</h2>
              <p>
                {isInvite
                  ? "Use a strong password you don't reuse elsewhere."
                  : 'Use a strong password you do not reuse elsewhere.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (user) {
    if (!initialized || authLoading) {
      return (
        <div className="auth-page">
          <div className="auth-container animate-fade-in">
            <div className="auth-form-section animate-slide-right">
              <div className="auth-form-wrapper">
                <div className="auth-header">
                  <h1 className="auth-brand-heading">
                    <RelixLockup height={39} />
                  </h1>
                  <p>Loading…</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="auth-page">
      <div className="auth-container animate-fade-in">
        <div className="auth-form-section animate-slide-right">
          <div className="auth-form-wrapper">
            <div className="auth-header">
              <h1 className="auth-brand-heading">
                <RelixLockup height={39} />
              </h1>
              {isSignUp && !showForgotPassword ? null : (
                <p>
                  {showForgotPassword
                    ? forgotPasswordSent
                      ? 'Check your email'
                      : 'Reset your password'
                    : 'Welcome back'}
                </p>
              )}
            </div>

            {showForgotPassword ? null : showPostInviteSignInHint ? (
              <div className="auth-success" style={{ marginBottom: '1.25rem' }}>
                <strong>You're all set.</strong> Your password is saved. Sign in with your email and
                the password you chose.
              </div>
            ) : null}

            {showForgotPassword ? null : (
              <div className="auth-toggle">
                <button
                  className={!isSignUp ? 'active' : ''}
                  onClick={() => {
                    setIsSignUp(false);
                    setError(null);
                    setSuccessMessage(null);
                    setShowPostInviteSignInHint(false);
                  }}
                >
                  Sign In
                </button>
                <button
                  className={isSignUp ? 'active' : ''}
                  onClick={() => {
                    if (!isSignUp) {
                      trackEvent('auth_signup_tab', { from: 'signin' });
                      trackEvent('auth_signup_to_audit', {
                        placement: 'embed',
                        pathname,
                      });
                    }
                    setIsSignUp(true);
                    setError(null);
                    setSuccessMessage(null);
                    setShowPostInviteSignInHint(false);
                  }}
                >
                  Sign Up
                </button>
                <div
                  className="toggle-slider"
                  style={{ transform: isSignUp ? 'translateX(100%)' : 'translateX(0%)' }}
                />
              </div>
            )}

            {error && <div className="auth-error">{error}</div>}
            {successMessage && <div className="auth-success">{successMessage}</div>}

            {/* Forgot password: success confirmation */}
            {showForgotPassword && forgotPasswordSent ? (
              <div className="auth-forgot-success">
                <p>
                  We've sent a password reset link to{' '}
                  <span className="auth-forgot-email-display">{formData.email}</span>
                </p>
                <p className="auth-forgot-hint">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    type="button"
                    className="auth-forgot-inline-link"
                    onClick={() => {
                      setForgotPasswordSent(false);
                      setError(null);
                    }}
                  >
                    try again
                  </button>
                  .
                </p>
                <button type="button" className="submit-btn" onClick={backToSignIn}>
                  Back to Sign In
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            ) : showForgotPassword ? (
              /* Forgot password: email entry form */
              <form onSubmit={handleForgotPassword}>
                <p className="auth-forgot-description">
                  Enter your email and we'll send you a link to set a new password.
                </p>
                <div className="form-field">
                  <label htmlFor="forgot-email">Email</label>
                  <div className="input-wrapper">
                    <Mail className="input-icon" />
                    <input
                      type="email"
                      id="forgot-email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <button type="submit" className="submit-btn" disabled={forgotPasswordLoading}>
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button type="button" className="auth-back-link" onClick={backToSignIn}>
                  Back to Sign In
                </button>
              </form>
            ) : isSignUp ? (
              <div className="solutions-page solutions-page--relix">
                <DeliveryAuditSection surface="auth_signup" />
              </div>
            ) : (
              /* Default: Sign In */
              <form onSubmit={handleSubmit}>
                <div className="form-field">
                  <label htmlFor="email">Email</label>
                  <div className="input-wrapper">
                    <Mail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="password">Password</label>
                  <div className="input-wrapper">
                    <Lock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="auth-forgot-link-row">
                  <button
                    type="button"
                    className="auth-forgot-link"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setError(null);
                      setSuccessMessage(null);
                    }}
                  >
                    Forgot password?
                  </button>
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Please wait...' : 'Sign In'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="auth-visual-section">
          <div className="visual-content animate-fade-in">
            <div className="visual-glow"></div>
            <h2>{brand.authVisualH2}</h2>
            <p>{brand.authVisualLead}</p>
            <ul className="auth-visual-bullets">
              {brand.authVisualBullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="auth-visual-tagline">{brand.authVisualTagline}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
